import { useEffect, useMemo, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { ExternalLinkIcon, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLegislationDocument } from '@/shared/hooks/api/radarScopeTier'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  buildEurLexUrlFromCelex,
  type LegislationCitationTarget,
} from './llmChatToolOutput'
import { resolveCitationHighlightRange } from './resolveCitationHighlight'

type LegislationViewerModalProps = {
  open: boolean
  target: LegislationCitationTarget | null
  onOpenChange: (open: boolean) => void
}

type HighlightBlockRangeTarget = {
  startBlockId: string
  endBlockId: string
}

type ResolveHighlightedBlockIdsResult = {
  highlightedBlockIds: Set<string>
  reason: 'missing_target' | 'missing_blocks' | 'ok'
}

export function resolveEffectiveHighlightTarget(input: {
  blocks: Array<{ blockId: string; blockOrder: number; rawText: string }>
  target: LegislationCitationTarget | null
}): HighlightBlockRangeTarget | null {
  if (!input.target) {
    return null
  }
  return resolveCitationHighlightRange({
    blocks: input.blocks,
    articleLabel: input.target.articleLabel,
    chunkText: input.target.chunkText,
    startBlockId: input.target.startBlockId,
    endBlockId: input.target.endBlockId,
  })
}

export function resolveHighlightedBlockIdsResult(input: {
  blocks: Array<{ blockId: string; blockOrder: number }>
  target: HighlightBlockRangeTarget | null
}): ResolveHighlightedBlockIdsResult {
  if (!input.target) {
    return { highlightedBlockIds: new Set<string>(), reason: 'missing_target' }
  }
  const start = input.blocks.find(
    (block) => block.blockId === input.target?.startBlockId
  )
  const end = input.blocks.find(
    (block) => block.blockId === input.target?.endBlockId
  )
  if (!start || !end) {
    return { highlightedBlockIds: new Set<string>(), reason: 'missing_blocks' }
  }
  const min = Math.min(start.blockOrder, end.blockOrder)
  const max = Math.max(start.blockOrder, end.blockOrder)
  return {
    highlightedBlockIds: new Set(
      input.blocks
        .filter((block) => block.blockOrder >= min && block.blockOrder <= max)
        .map((block) => block.blockId)
    ),
    reason: 'ok',
  }
}

export function resolveHighlightedBlockIds(input: {
  blocks: Array<{ blockId: string; blockOrder: number }>
  target: HighlightBlockRangeTarget | null
}): Set<string> {
  return resolveHighlightedBlockIdsResult(input).highlightedBlockIds
}

export function resolveFirstHighlightedBlockId(input: {
  blocks: Array<{ blockId: string; blockOrder: number }>
  highlightedBlockIds: Set<string>
}): string | null {
  const firstBlock = [...input.blocks]
    .filter((block) => input.highlightedBlockIds.has(block.blockId))
    .sort((left, right) => left.blockOrder - right.blockOrder)[0]
  return firstBlock?.blockId ?? null
}

export const SCROLL_INTO_VIEW_TOP_BUFFER_RATIO = 0.33

export function resolveScrollTopBufferPx(viewport: HTMLElement): number {
  return viewport.clientHeight * SCROLL_INTO_VIEW_TOP_BUFFER_RATIO
}

export function isViewportScrollable(viewport: HTMLElement): boolean {
  const { overflowY } = getComputedStyle(viewport)
  if (overflowY !== 'auto' && overflowY !== 'scroll') {
    return false
  }
  return viewport.scrollHeight > viewport.clientHeight + 1
}

export function computeScrollTopToRevealElement(input: {
  viewport: HTMLElement
  element: HTMLElement
  topPaddingPx?: number
}): number {
  const viewportRect = input.viewport.getBoundingClientRect()
  const elementRect = input.element.getBoundingClientRect()
  const topPadding = input.topPaddingPx ?? resolveScrollTopBufferPx(input.viewport)
  const elementOffset =
    elementRect.top - viewportRect.top + input.viewport.scrollTop
  return Math.max(0, elementOffset - topPadding)
}

export function isHighlightedBlockVisibleInViewport(
  viewport: HTMLElement,
  element: HTMLElement
): boolean {
  const viewportRect = viewport.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  const topBuffer = resolveScrollTopBufferPx(viewport)
  return (
    elementRect.top >= viewportRect.top + topBuffer - 4 &&
    elementRect.top < viewportRect.bottom - 24
  )
}

export function scrollHighlightedBlockIntoViewport(input: {
  viewport: HTMLElement
  blockId: string
  behavior?: ScrollBehavior
}): boolean {
  const element = input.viewport.querySelector<HTMLElement>(
    `[data-legislation-block-id="${input.blockId}"]`
  )
  if (!element) {
    return false
  }

  const behavior = input.behavior ?? 'auto'

  if (isViewportScrollable(input.viewport)) {
    input.viewport.scrollTo({
      top: computeScrollTopToRevealElement({
        viewport: input.viewport,
        element,
      }),
      behavior,
    })
    return true
  }

  element.scrollIntoView({
    block: 'start',
    behavior,
  })
  return true
}

export function LegislationViewerModal({
  open,
  target,
  onOpenChange,
}: LegislationViewerModalProps) {
  const { t } = useTranslation('llmChat')
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const query = useLegislationDocument(target?.documentId ?? null, {
    enabled: open && target != null,
  })
  const eurLexUrl = useMemo(() => {
    if (!target) {
      return null
    }
    return buildEurLexUrlFromCelex(target.celex)
  }, [target])

  const effectiveHighlightTarget = useMemo(() => {
    const blocks = query.data?.blocks
    if (!blocks || !target) {
      return null
    }
    return resolveEffectiveHighlightTarget({
      blocks,
      target,
    })
  }, [query.data?.blocks, target])

  const highlightScrollKey = useMemo(() => {
    if (!effectiveHighlightTarget) {
      return null
    }
    return `${effectiveHighlightTarget.startBlockId}::${effectiveHighlightTarget.endBlockId}`
  }, [effectiveHighlightTarget])

  const highlightedBlockIds = useMemo(() => {
    const blocks = query.data?.blocks
    if (!blocks) {
      return new Set<string>()
    }
    const result = resolveHighlightedBlockIdsResult({
      blocks,
      target: effectiveHighlightTarget,
    })
    if (result.reason === 'missing_blocks') {
      Sentry.captureException(
        new Error('Legislation viewer highlight block ids not found in document'),
        {
          extra: {
            celex: query.data?.celex ?? target?.celex ?? null,
            documentId: query.data?.documentId ?? target?.documentId ?? null,
            startBlockId: effectiveHighlightTarget?.startBlockId ?? null,
            endBlockId: effectiveHighlightTarget?.endBlockId ?? null,
          },
        }
      )
    }
    return result.highlightedBlockIds
  }, [
    effectiveHighlightTarget,
    query.data?.blocks,
    query.data?.celex,
    query.data?.documentId,
    target,
  ])

  const firstHighlightedBlockId = useMemo(() => {
    const blocks = query.data?.blocks
    if (!blocks || highlightedBlockIds.size === 0) {
      return null
    }
    return resolveFirstHighlightedBlockId({
      blocks,
      highlightedBlockIds,
    })
  }, [highlightedBlockIds, query.data?.blocks])

  useEffect(() => {
    if (
      !open ||
      !highlightScrollKey ||
      !firstHighlightedBlockId ||
      query.isLoading ||
      !query.data?.blocks?.length
    ) {
      return
    }

    let cancelled = false
    let frameId = 0
    let retryTimeout: number | undefined
    let attempt = 0

    const tryScroll = () => {
      if (cancelled || attempt >= 30) {
        return
      }
      attempt += 1

      const viewport = scrollViewportRef.current
      if (!viewport) {
        frameId = window.requestAnimationFrame(tryScroll)
        return
      }

      const scrolled = scrollHighlightedBlockIntoViewport({
        viewport,
        blockId: firstHighlightedBlockId,
        behavior: 'auto',
      })
      const element = viewport.querySelector<HTMLElement>(
        `[data-legislation-block-id="${firstHighlightedBlockId}"]`
      )
      if (
        scrolled &&
        element &&
        isHighlightedBlockVisibleInViewport(viewport, element)
      ) {
        return
      }

      retryTimeout = window.setTimeout(() => {
        frameId = window.requestAnimationFrame(tryScroll)
      }, 50)
    }

    const resizeObserver = new ResizeObserver(() => {
      attempt = 0
      tryScroll()
    })

    frameId = window.requestAnimationFrame(() => {
      const viewport = scrollViewportRef.current
      if (viewport) {
        resizeObserver.observe(viewport)
      }
      tryScroll()
    })

    return () => {
      cancelled = true
      window.cancelAnimationFrame(frameId)
      if (retryTimeout !== undefined) {
        window.clearTimeout(retryTimeout)
      }
      resizeObserver.disconnect()
    }
  }, [
    open,
    highlightScrollKey,
    firstHighlightedBlockId,
    query.isLoading,
    query.data?.blocks?.length,
  ])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[88vh] max-w-[calc(100%-4rem)] overflow-hidden p-0 sm:max-w-6xl'>
        <DialogHeader className='border-b border-border/70 px-5 pe-14 py-4'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div className='space-y-1'>
              <DialogTitle>
                {query.data?.title ?? t('legislationViewer.title')}
              </DialogTitle>
              <DialogDescription>
                {target?.articleLabel ?? t('legislationViewer.subtitle')}
              </DialogDescription>
            </div>
            {eurLexUrl ? (
              <Button type='button' variant='outline' size='sm' asChild>
                <a href={eurLexUrl} target='_blank' rel='noreferrer noopener'>
                  {t('legislationViewer.openOnEurLex')}
                  <ExternalLinkIcon className='ms-1 size-3.5' />
                </a>
              </Button>
            ) : null}
          </div>
        </DialogHeader>

        <ScrollArea
          viewportRef={scrollViewportRef}
          className='h-[68vh] px-5 py-4'
        >
          {query.isLoading ? (
            <div className='flex items-center justify-center py-10 text-muted-foreground'>
              <Loader2 className='size-5 animate-spin' />
              <span className='ms-2 text-sm'>{t('legislationViewer.loading')}</span>
            </div>
          ) : query.isError ? (
            <p className='py-6 text-sm text-muted-foreground'>
              {t('legislationViewer.loadError')}
            </p>
          ) : !query.data || query.data.blocks.length === 0 ? (
            <p className='py-6 text-sm text-muted-foreground'>
              {t('legislationViewer.empty')}
            </p>
          ) : (
            <div className='space-y-2'>
              {query.data.blocks.map((block) => {
                const isHighlighted = highlightedBlockIds.has(block.blockId)
                return (
                  <p
                    key={block.blockId}
                    data-legislation-block-id={block.blockId}
                    className={
                      isHighlighted
                        ? 'whitespace-pre-wrap rounded-md bg-emerald-900/35 px-2 py-1 text-sm leading-relaxed ring-1 ring-emerald-500/35'
                        : 'whitespace-pre-wrap text-sm leading-relaxed'
                    }
                  >
                    {block.rawText}
                  </p>
                )
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
